import styles from './CoachProfilePage.module.css';
import { useUser } from '../../../Context/useUser';
import { useEffect, useState } from 'react';
import API from '../../../Utils/api';

import Avatar from 'react-avatar';
import { toast } from 'react-toastify';
import { useParams } from 'react-router';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import { FaCreditCard, FaPassport } from 'react-icons/fa';
import { FaLocationDot, FaBuildingColumns } from 'react-icons/fa6';
import { IoNotifications } from 'react-icons/io5';
import { MdOutlineSecurity } from 'react-icons/md';
import { TbChartCandle } from 'react-icons/tb';
import { BsCashCoin } from 'react-icons/bs';
import { BsBank } from 'react-icons/bs';
import { FaRegCreditCard } from 'react-icons/fa6';

import { Badge, Button, Col, Container, Row } from 'react-bootstrap';
import Accordion from 'react-bootstrap/Accordion';
import EditableField from '../../../Components/EditableField/EditableField';
import Table from '../../../Components/Table/Table';
import { useForm } from 'react-hook-form';

export default function CoachProfilePage() {
    const { user, setUser } = useUser();

    const { coachId } = useParams();

    const visitedUser = useVisitingUser(coachId);

    const [viewMode, setViewMode] = useState('general'); // 'security', 'billing', 'notifications'
    const [currentUser, setCurrentUser] = useState(null);
    const [coachStats, setCoachStats] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isSavingProfile, setIsSavingProfile] = useState(false);

    useEffect(() => {
        setCurrentUser(coachId ? visitedUser : user);
        setHasUnsavedChanges(false);
    }, [visitedUser, coachId, user]);

    const queueUserUpdate = userD => {
        setCurrentUser(userD);
        setHasUnsavedChanges(true);
    };

    const saveProfile = async () => {
        if (!currentUser?._id) return;
        setIsSavingProfile(true);
        try {
            const payload = { ...currentUser };
            if (!payload.gender) {
                delete payload.gender;
            }
            if (user._id === currentUser._id) {
                const { data } = await API.updateMyself(payload);
                setUser({ ...user, ...data });
                setCurrentUser(data);
            } else {
                const { data } = await API.updateUser(currentUser._id, payload);
                setCurrentUser(data);
            }
            setHasUnsavedChanges(false);
            toast.success('Profile updated.');
        } catch (e) {
            toast.error('Failed to save profile.');
        } finally {
            setIsSavingProfile(false);
        }
    };

    useEffect(() => {
        if (!currentUser?._id) return;
        const fetchStats = async () => {
            try {
                const { data } = await API.getWorkoutStatsByCoachId(currentUser._id);
                setCoachStats(data);
            } catch (e) {
                setCoachStats(null);
            }
        };
        fetchStats();
    }, [currentUser?._id]);

    const paymentColumns = [
        {
            Header: 'Transaction ID',
            accessor: 'Transaction Id'
        },
        { Header: 'Payment Date', accessor: 'date' },
        {
            Header: 'Amount (€)',
            accessor: 'Amount'
        },
        {
            Header: 'Payment Status',
            accessor: 'Status',
            Cell: ({ value }) => (
                <div>
                    <Badge pill bg='success'>
                        Paid
                    </Badge>
                </div>
            )
        }
    ];

    const { register, handleSubmit, reset } = useForm({
        defaultValues: {
            changesToAccount: user?.notifications?.changesToAccount ?? false,
            promotionalOffers: user?.notifications?.promotionalOffers ?? false
        }
    });

    const {
        register: registerPassword,
        handleSubmit: handlePasswordSubmit,
        reset: resetPasswordForm,
        formState: { errors: passwordErrors, isSubmitting }
    } = useForm();

    const onPasswordChange = async ({ currentPassword, newPassword, confirmPassword }) => {
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match.');
            return;
        }
        try {
            await API.updatePassword({
                userId: currentUser._id,
                oldPassword: currentPassword,
                newPassword
            });
            toast.success('Password updated successfully.');
            resetPasswordForm();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Failed to update password.');
        }
    };

    useEffect(() => {
        if (currentUser) {
            reset({
                changesToAccount: currentUser.notifications?.changesToAccount ?? false,
                promotionalOffers: currentUser.notifications?.promotionalOffers ?? false
            });
        }
    }, [currentUser, reset]);

    const onNotificationFormSubmit = data => {
        // Convert checkbox values to booleans
        const updatedNotifications = {
            changesToAccount: !!data.changesToAccount,
            promotionalOffers: !!data.promotionalOffers
        };
        const updatedUser = {
            ...currentUser,
            notifications: {
                ...currentUser.notifications,
                ...updatedNotifications
            }
        };
        queueUserUpdate(updatedUser);
    };

    if (!currentUser) return;

    return (
        <div className={styles.pageWrap}>
            <div className={styles.leftSide + ' island'}>
                <Avatar name={`${currentUser.firstName} ${currentUser.lastName}`} round={true} />
                <div className={styles.name}>
                    {currentUser.title} {currentUser.firstName} {currentUser.lastName}
                </div>
                <div className={styles.email}>{currentUser.email}</div>
                {
                    <div title='Location'>
                        <FaLocationDot style={{ color: 'red' }} />{' '}
                        {currentUser?.location ?? 'Not Provided'}
                    </div>
                }
                {
                    <div title='Swimming Club'>
                        <FaBuildingColumns style={{ color: 'var(--primary-color)' }} />{' '}
                        {currentUser.swimmingClub ?? 'Not Provided'}
                    </div>
                }
                <div className={styles.about}>
                    <div className={styles.aboutTitle}>About</div>
                    <Badge pill bg='info'>
                        Total Athletes: {coachStats?.athletes ?? 0}
                    </Badge>
                    <Badge pill className={styles.testsBadge}>
                        Total Groups: {coachStats?.groups ?? 0}
                    </Badge>
                    <Badge pill className={styles.racesBadge}>
                        Total Workout Plans Created: {coachStats?.programs ?? 0}
                    </Badge>
                </div>
            </div>
            <div className={styles.rightSide}>
                <div className={styles.topBar}>
                    <div className={styles.viewToggle + ' island'}>
                        <button
                            className={`${styles.toggleBtn} ${
                                viewMode === 'general' ? styles.active : ''
                            }`}
                            onClick={() => setViewMode('general')}
                            title='View General'
                        >
                            <TbChartCandle size={20} />
                            <span>General</span>
                        </button>
                        <button
                            className={`${styles.toggleBtn} ${
                                viewMode === 'security' ? styles.active : ''
                            }`}
                            onClick={() => setViewMode('security')}
                            title='View Security Settings'
                        >
                            <MdOutlineSecurity size={20} />
                            <span>Security</span>
                        </button>
                        <button
                            className={`${styles.toggleBtn} ${
                                viewMode === 'billing' ? styles.active : ''
                            }`}
                            onClick={() => setViewMode('billing')}
                            title='View Billing Information'
                        >
                            <FaCreditCard size={20} />
                            <span>Billing</span>
                        </button>
                        <button
                            className={`${styles.toggleBtn} ${
                                viewMode === 'notifications' ? styles.active : ''
                            }`}
                            onClick={() => setViewMode('notifications')}
                            title='View Notification Settings'
                        >
                            <IoNotifications size={20} />
                            <span>Notifications</span>
                        </button>
                    </div>
                </div>
                <div className={styles.selectedMenuItem}>
                    {viewMode === 'general' && (
                        <>
                            <Accordion
                                className={styles.accordion + ' island'}
                                defaultActiveKey={['0', '1', '2']}
                                alwaysOpen
                            >
                                <Accordion.Item eventKey='0'>
                                    <Accordion.Header>
                                        Basic Information
                                        <FaPassport style={{ marginLeft: '5px' }} size={20} />
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <Container fluid>
                                            <Row className={styles.infoRow}>
                                                <Col>
                                                    <b>Email: </b>
                                                    <span>{currentUser.email}</span>
                                                </Col>
                                                <Col>
                                                    <b>Phone Number:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.phoneNumber}
                                                        placeholder='Enter Phone Number'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                phoneNumber: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className={styles.infoRow}>
                                                <Col>
                                                    <b>Title:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.title}
                                                        placeholder='Enter Title'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                title: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                                <Col>
                                                    <b>First Name:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.firstName}
                                                        placeholder='Enter First Name'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                firstName: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                                <Col>
                                                    <b>Last Name:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.lastName}
                                                        placeholder='Enter Last Name'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                lastName: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className={styles.infoRow}>
                                                <Col>
                                                    <b>Birth Year:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.birthYear}
                                                        placeholder='Enter Birth Year'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                birthYear: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                                <Col>
                                                    <b>Gender:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.gender}
                                                        placeholder='Select Gender'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                gender: val
                                                            });
                                                        }}
                                                        type='select'
                                                        options={[
                                                            { value: '', label: 'Select Gender' },
                                                            { value: 'Female', label: 'Female' },
                                                            { value: 'Male', label: 'Male' },
                                                            { value: 'Other', label: 'Other' }
                                                        ]}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className={styles.infoRow}>
                                                <Col>
                                                    <b>Location:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.location}
                                                        placeholder='Enter Location'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                location: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                                <Col>
                                                    <b>Swimming Club:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.swimmingClub}
                                                        placeholder='Enter Swimming Club'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                swimmingClub: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                            </Row>
                                        </Container>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        </>
                    )}
                    {viewMode === 'security' && (
                        <div className={styles.securityPage}>
                            <div className={styles.changePasswordArea + ' island'}>
                                <div className={styles.header}>Change Password</div>
                                <form
                                    className={styles.changePasswordContent + ' form-group'}
                                    onSubmit={handlePasswordSubmit(onPasswordChange)}
                                >
                                    <div className={styles.changePasswordField}>
                                        <label htmlFor='currentPassword'>Current Password</label>
                                        <input
                                            type='password'
                                            className='form-control'
                                            id='currentPassword'
                                            placeholder='Enter current password'
                                            {...registerPassword('currentPassword', {
                                                required: true
                                            })}
                                        />
                                        {passwordErrors.currentPassword && (
                                            <span className={styles.error}>
                                                Current password is required
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.changePasswordField}>
                                        <label htmlFor='newPassword'>New Password</label>
                                        <input
                                            type='password'
                                            className='form-control'
                                            id='newPassword'
                                            placeholder='Enter new password'
                                            {...registerPassword('newPassword', {
                                                required: true,
                                                minLength: 5
                                            })}
                                        />
                                        {passwordErrors.newPassword && (
                                            <span className={styles.error}>
                                                New password is required (min 6 chars)
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.changePasswordField}>
                                        <label htmlFor='confirmPassword'>
                                            Confirm New Password
                                        </label>
                                        <input
                                            type='password'
                                            className='form-control'
                                            id='confirmPassword'
                                            placeholder='Confirm new password'
                                            {...registerPassword('confirmPassword', {
                                                required: true
                                            })}
                                        />
                                        {passwordErrors.confirmPassword && (
                                            <span className={styles.error}>
                                                Please confirm your new password
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        className={`${styles.changePasswordFormButton} btn btn-primary`}
                                        type='submit'
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Submit'}
                                    </button>
                                </form>
                            </div>
                            <div className={styles.disableAccountArea + ' island'}>
                                <div className={styles.header}>Disable Account</div>
                                <div className={styles.disableAccountContent}>
                                    <div>
                                        Disable your account is a non permanent action and can only
                                        be undone by support. If you are sure you want to disable
                                        your account, select the button below.
                                    </div>
                                    <Button
                                        variant='outline-danger'
                                        onClick={() =>
                                            toast.error(
                                                'Feature temporarily disabled. Please contact support.'
                                            )
                                        }
                                    >
                                        I understand, disable my account
                                    </Button>{' '}
                                </div>
                            </div>
                        </div>
                    )}
                    {viewMode === 'billing' && (
                        <div className={styles.billingPage}>
                            <div className={styles.topCards}>
                                <div
                                    className={styles.billingCard + ' island'}
                                    style={{ borderLeft: '5px solid var(--base-color)' }}
                                >
                                    <small>Current monthly bill</small>
                                    <span className={styles.billingCardSpan}>0€</span>
                                </div>
                                <div
                                    className={styles.billingCard + ' island'}
                                    style={{ borderLeft: '5px solid var(--primary-mss-color)' }}
                                >
                                    <small>Next payment due</small>
                                    <span className={styles.billingCardSpan}>N/A</span>
                                </div>
                                <div
                                    className={styles.billingCard + ' island'}
                                    style={{ borderLeft: '5px solid #4cd964' }}
                                >
                                    <small>Current plan</small>
                                    <span className={styles.billingCardSpan}>Free</span>
                                </div>
                            </div>
                            <div className={styles.paymentMethods}>
                                <Accordion
                                    className={styles.accordion + ' island'}
                                    defaultActiveKey={['0', '1', '2']}
                                    alwaysOpen
                                >
                                    <Accordion.Item eventKey='0'>
                                        <Accordion.Header>Payment Methods</Accordion.Header>
                                        <Accordion.Body>
                                            <div className={styles.paymentMethodsArea}>
                                                <div className={styles.paymentMethodRow}>
                                                    <span>
                                                        <BsCashCoin
                                                            size={17}
                                                            style={{ marginRight: '5px' }}
                                                        ></BsCashCoin>{' '}
                                                        Cash
                                                    </span>
                                                    <Badge
                                                        pill
                                                        style={{ fontSize: '13px' }}
                                                        bg='secondary'
                                                    >
                                                        Disabled
                                                    </Badge>{' '}
                                                </div>
                                                <div className={styles.paymentMethodRow}>
                                                    <span>
                                                        <BsBank
                                                            size={17}
                                                            style={{ marginRight: '5px' }}
                                                        />
                                                        Bank Transfer
                                                    </span>
                                                    <Badge
                                                        pill
                                                        style={{ fontSize: '13px' }}
                                                        bg='secondary'
                                                    >
                                                        Disabled
                                                    </Badge>
                                                </div>
                                                <div className={styles.paymentMethodRow}>
                                                    <span>
                                                        <FaRegCreditCard
                                                            size={17}
                                                            style={{ marginRight: '5px' }}
                                                        />
                                                        Debit Card
                                                    </span>
                                                    <Badge
                                                        pill
                                                        style={{ fontSize: '13px' }}
                                                        bg='secondary'
                                                    >
                                                        Disabled
                                                    </Badge>{' '}
                                                </div>
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                    <Accordion.Item eventKey='1'>
                                        <Accordion.Header>Payment History</Accordion.Header>
                                        <Accordion.Body>
                                            <Table
                                                columns={paymentColumns}
                                                data={[
                                                    {
                                                        'Transaction Id': '#000',
                                                        date: '-',
                                                        Amount: '0.00',
                                                        Status: 'Paid'
                                                    }
                                                ]}
                                            ></Table>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </div>
                        </div>
                    )}
                    {viewMode === 'notifications' && (
                        <div className={styles.notificationsPage}>
                            <div className={styles.emailNotificationsArea + ' island'}>
                                <div className={styles.emailNotificationsHeader}>
                                    Email Notifications
                                </div>
                                <div className={styles.emailNotificationsContent}>
                                    <div className={styles.emailNotificationField}>
                                        <label
                                            className={styles.notificationFieldLabel}
                                            for='currentEmail'
                                        >
                                            Default notification Email
                                        </label>
                                        <input
                                            type='button'
                                            disabled={true}
                                            id='currentEmail'
                                            value={currentUser.email}
                                        ></input>
                                    </div>
                                    <form onSubmit={handleSubmit(onNotificationFormSubmit)}>
                                        <div className={styles.emailNotificationField}>
                                            <label
                                                className={styles.notificationFieldLabel}
                                                for='changesToAccount'
                                            >
                                                Changes made to your account
                                            </label>
                                            <input
                                                type='checkbox'
                                                id='changesToAccount'
                                                {...register('changesToAccount')}
                                                defaultChecked={
                                                    currentUser.notifications?.changesToAccount ??
                                                    false
                                                }
                                            />{' '}
                                        </div>
                                        <div className={styles.emailNotificationField}>
                                            <label
                                                className={styles.notificationFieldLabel}
                                                for='promotionalOffers'
                                            >
                                                Marketing and Promotional Offers
                                            </label>
                                            <input
                                                type='checkbox'
                                                id='promotionalOffers'
                                                {...register('promotionalOffers')}
                                                defaultChecked={
                                                    currentUser.notifications?.promotionalOffers ??
                                                    false
                                                }
                                            />
                                        </div>
                                        <button
                                            className={`${styles.changePasswordFormButton} btn btn-primary`}
                                        >
                                            Submit
                                        </button>
                                    </form>
                                </div>
                            </div>
                            <div className={styles.disableAccountArea + ' island'}>
                                <div className={styles.header}>Disable Account</div>
                                <div className={styles.disableAccountContent}>
                                    <div>
                                        Disable your account is a non permanent action and can only
                                        be undone by support. If you are sure you want to disable
                                        your account, select the button below.
                                    </div>
                                    <Button variant='outline-danger'>
                                        I understand, disable my account
                                    </Button>{' '}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        className='btn btn-primary'
                        onClick={saveProfile}
                        disabled={!hasUnsavedChanges || isSavingProfile}
                    >
                        {isSavingProfile ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
}
